import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentDistributionPanel } from '@/components/workflows/DocumentDistributionPanel';
import { GovernanceFilingPanel } from '@/components/workflows/GovernanceFilingPanel';

interface ResolutionPhase2Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

export default function ResolutionPhase2({ taskId, unaId, task, phaseData, onPhaseComplete }: ResolutionPhase2Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [filingComplete, setFilingComplete] = useState(false);
  const [distributionRecipients, setDistributionRecipients] = useState<any[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [task]);

  useEffect(() => {
    loadDocuments();
    extractDistributionList();
  }, [taskId]);

  useEffect(() => {
    checkCompletionStatus();
  }, [documents]);

  function extractDistributionList() {
    const workflowData = task.workflow_data || {};
    const distributionList = workflowData.distribution_list || [];
    
    // Ensure distributionList is always an array
    if (!Array.isArray(distributionList)) {
      console.warn('distribution_list is not an array:', distributionList);
      setDistributionRecipients([]);
      return;
    }
    
    // Convert distribution list to recipient format
    const recipients = distributionList.map((item: any) => ({
      id: `external_${item.email}`,
      name: item.name,
      email: item.email,
      type: 'external'
    }));
    
    setDistributionRecipients(recipients);
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', taskId)
        .in('status', ['finalized', 'final'])
        .order('generated_at', { ascending: true });

      if (error) throw error;
      
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function checkCompletionStatus() {
    await checkDistributionComplete();
    await checkFilingComplete();
  }

  async function checkDistributionComplete(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('id, status')
        .eq('task_id', taskId);

      if (error) throw error;

      const isComplete = data && data.length > 0 && data.some(d => d.status === 'sent');
      setDistributionComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking distribution status:', error);
      return false;
    }
  }

  async function checkFilingComplete(): Promise<boolean> {
    try {
      const { data: govDocs, error } = await supabase
        .from('governance_documents')
        .select('id')
        .eq('generated_from_task_id', taskId);

      if (error) throw error;

      // Check if at least one document has been filed
      const isComplete = govDocs && govDocs.length >= 1;
      
      setFilingComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking filing status:', error);
      return false;
    }
  }

  async function handleDistributionComplete() {
    await checkDistributionComplete();
  }

  async function handleFilingComplete() {
    await checkFilingComplete();
  }

  async function handleMarkComplete() {
    try {
      setIsCompleting(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Resolution record is created by the complete-workflow-phase edge function
      // Use the complete_task RPC function (security definer, bypasses RLS)
      const { error } = await supabase.rpc('complete_task', {
        p_task_id: taskId,
        p_user_id: user.id
      });

      if (error) throw error;

      setIsCompleted(true);

      // Governance Health prefilled flow: if this resolution was launched from
      // a compliance item, satisfy that item so the checklist updates without
      // the member returning to the health page. Silent failure — nightly
      // refresh will catch anything missed.
      const ghItem = task?.workflow_data?.gh_item;
      if (ghItem) {
        try {
          const { data: resolutionRow } = await supabase
            .from('una_resolutions' as any)
            .select('id')
            .eq('task_id', taskId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          const resolutionId = (resolutionRow as any)?.id ?? null;
          if (resolutionId) {
            await supabase.rpc('validate_and_satisfy_item', {
              _item_id: ghItem,
              _evidence_type: 'resolution',
              _evidence_id: resolutionId,
            });
            await supabase.rpc('compute_una_governance_score', {
              _una_id: unaId,
              _period_year: 2026,
            });
          } else {
            console.warn('[GH] No resolution row found for task; skipping satisfy');
          }
        } catch (ghErr) {
          console.warn('[GH] Failed to satisfy compliance item', ghErr);
        }
      }

      toast({
        title: 'Resolution Complete',
        description: 'Resolution workflow completed and filed to Resolutions & Actions.',
      });

      onPhaseComplete?.();
    } catch (error: any) {
      console.error('Error completing workflow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete workflow',
        variant: 'destructive',
      });
    } finally {
      setIsCompleting(false);
    }
  }

  // Distribution is optional, only filing is required
  const allComplete = filingComplete;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <CardTitle>Phase 2: File & Distribute</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Final phase: distribute resolution to stakeholders and file in governance archives.
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Distribute resolution to distribution list and UNA members</li>
              <li>File resolution in governance documents</li>
              <li>Complete resolution workflow</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phase 2 Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Document Distribution</span>
            {distributionComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Governance Filing</span>
            {filingComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Document Distribution */}
      <DocumentDistributionPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        additionalRecipients={distributionRecipients}
        onDistributionComplete={handleDistributionComplete}
      />

      {/* Governance Filing */}
      <GovernanceFilingPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        workflowKey="writing_resolution"
        onFilingComplete={handleFilingComplete}
      />

      {/* Complete Phase - Always show, but disable when not ready */}
      {isCompleted ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Resolution Workflow Completed</p>
                <p className="text-sm text-green-700">All documents have been distributed and filed.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50'}>
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {allComplete ? 'All Phase 2 Steps Complete' : 'Complete Resolution Workflow'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {allComplete 
                    ? `${distributionComplete ? 'Resolution distributed and filed.' : 'Resolution filed.'} Ready to complete workflow.`
                    : 'File at least one document to governance to complete. Distribution is optional.'
                  }
                </p>
              </div>
              <Button 
                onClick={handleMarkComplete} 
                disabled={!allComplete || isCompleting}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Complete Resolution Workflow'
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}